import { Box, Grid, Typography, Alert, TextField } from '@mui/material';
import { useI18nContext } from '@/i18n/i18n-react';
import { CategorySelectionCard } from '@/components/CategorySelectionCard';

interface Category {
    id: number;
    name: string;
    icon: string;
    group?: string;
    active: number;
}

interface CategorySelectionSectionProps {
    availableCategories: Category[];
    selectedCategoryIds: number[];
    pendingRequestCategoryIds: Set<number>;
    activeTab: 'join' | 'leave';
    message: string;
    onCategoryToggle: (id: number) => void;
    onMessageChange: (message: string) => void;
}

export const CategorySelectionSection = ({
    availableCategories,
    selectedCategoryIds,
    pendingRequestCategoryIds,
    activeTab,
    message,
    onCategoryToggle,
    onMessageChange,
}: CategorySelectionSectionProps) => {
    const { LL } = useI18nContext();

    if (availableCategories.length === 0) {
        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity="info">
                    {activeTab === 'join'
                        ? LL.categoryRequests.youAreAlreadyMember()
                        : LL.categoryRequests.youAreNotMember()}
                </Alert>
            </Box>
        );
    }

    // Group categories by their group
    const groupedCategories = availableCategories.reduce(
        (groups, cat) => {
            const group = cat.group || 'Other';
            if (!groups[group]) groups[group] = [];
            groups[group].push(cat);
            return groups;
        },
        {} as Record<string, Category[]>
    );

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                {LL.categoryRequests.selectCategoryHelp()}
            </Typography>

            {/* Category Groups */}
            {Object.entries(groupedCategories).map(([groupName, groupCategories]) => (
                <Box key={groupName} sx={{ mb: 2 }}>
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ mb: 1, display: 'block' }}
                    >
                        {groupName}
                    </Typography>
                    <Grid container spacing={1}>
                        {groupCategories.map((cat) => {
                            const hasPendingRequest = pendingRequestCategoryIds.has(cat.id);
                            const isSelected = selectedCategoryIds.includes(cat.id);

                            return (
                                <CategorySelectionCard
                                    key={cat.id}
                                    category={cat}
                                    isSelected={isSelected}
                                    hasPendingRequest={hasPendingRequest}
                                    onToggle={onCategoryToggle}
                                />
                            );
                        })}
                    </Grid>
                </Box>
            ))}

            {/* Message Field */}
            <TextField
                label={LL.categoryRequests.requestMessage()}
                multiline
                rows={3}
                value={message}
                onChange={(e) => onMessageChange(e.target.value)}
                helperText={LL.categoryRequests.requestMessageHelp()}
                fullWidth
                sx={{ mt: 1 }}
            />
        </Box>
    );
};
